import { ICreateOptionDto } from '@/api/services/options.service';
import { ComandaSplit } from '@/enums/ComandaSplit';
import {
  ICatalogCreatedRefs,
  IItemCreate,
  IItemPatch,
  IOptionLinkInput,
  ISizeUpsert,
} from '@/interfaces/ICatalogV2';
import { IItem } from '@/interfaces/IItem';
import { IOption } from '@/interfaces/IOption';
import { ISimpleHourRange } from '@/interfaces/ISimpleHourRange';
import { formatMoneyInput, parseMoneyInput } from '@/domain/store-setup/money';
import { sizeOption, sortedSizes } from './catalog-filters';
import {
  copy,
  formChangedKeys,
  formRebaseOnRemote,
  IFormModel,
  IRemoteRebase,
  sameFormKey,
} from './form-model';
import { TPriceFieldError } from './price-form';

/**
 * The product editor's form (plan §5.12): texts as typed, compared and sent
 * normalised. `diffProductForm` sends only the keys that changed, so a save
 * never overwrites what another device changed in other fields.
 */
export interface ISizeRowForm {
  /**
   * On-screen identity (the `v-for` key), sent as `ref` for new rows; kept
   * by every rebase (`keepRowKeys`).
   */
  key: string;
  /** null = a new size. */
  id: number | null;
  title: string;
  priceText: string;
  /** null = no discount. */
  discountText: string | null;
  active: boolean;
  picture: string | null;
  description: string | null;
}

export type IPriceForm =
  | { mode: 'single'; priceText: string; discountText: string | null }
  | { mode: 'sizes'; title: string; rows: ISizeRowForm[] };

export interface ILinkOverrides {
  min: number | null;
  max: number | null;
  mandatory: boolean | null;
  multiple: boolean | null;
}

export interface IOptionLinkRow {
  /**
   * On-screen identity (the `v-for` key), sent as `ref` for drafts; kept by
   * every rebase (`keepRowKeys`).
   */
  key: string;
  /** null for a draft (a new group created on save). */
  optionId: number | null;
  draft: ICreateOptionDto | null;
  active: boolean;
  /** Legacy per-link rule (read-only; none exist today). */
  overrides: ILinkOverrides | null;
  /** The linked group, for display; null for drafts. */
  option: IOption | null;
}

export interface IProductForm {
  name: string;
  description: string;
  categoryId: number;
  picture: string | null;
  allergens: number;
  recipe: string;
  vatText: string;
  price: IPriceForm;
  visible: boolean;
  hiddenBranchIds: number[];
  hourRanges: ISimpleHourRange[] | null;
  optionLinks: IOptionLinkRow[];
  comandaPrinters: number[] | null;
  comandaSplit: ComandaSplit;
  /** Kept out of the storefront's «Lo más pedido» (owner 2026-11-03). */
  popularHidden: boolean;
  reportingCategoryId: number | null;
}

export type TProductFormKey = keyof IProductForm;

export const PRODUCT_FORM_KEYS: readonly TProductFormKey[] = [
  'name',
  'description',
  'categoryId',
  'picture',
  'allergens',
  'recipe',
  'vatText',
  'price',
  'visible',
  'hiddenBranchIds',
  'hourRanges',
  'optionLinks',
  'comandaPrinters',
  'comandaSplit',
  'popularHidden',
  'reportingCategoryId',
];

export const MAX_NAME_LENGTH = 128;
/** Description and preparation notes (the API's `MAX_LONG_TEXT`). */
export const MAX_LONG_TEXT_LENGTH = 10000;
export const MAX_SIZE_TITLE_LENGTH = 255;
/** Adding sizes stops here (existing stores may have more). */
export const MAX_SIZES = 20;
/** Longer descriptions get the «primeras 2 líneas» hint. */
export const DESCRIPTION_HINT_LENGTH = 160;

/**
 * Keys of rows made in the editor (`new-s…`, `new-o…`, `new-d…`), never
 * equal to a loaded row's `s<id>` / `o<id>`. A row sent as new (a new size,
 * a draft) sends its key as `ref`.
 */
let keySequence = 0;
function nextKey(prefix: 's' | 'o' | 'd'): string {
  keySequence += 1;
  return `new-${prefix}${keySequence}`;
}

function moneyText(value: number | null | undefined, country: string): string {
  return value == null ? '' : formatMoneyInput(Number(value), country);
}

function readMoney(text: string | null): number | null {
  return text === null ? null : parseMoneyInput(text, { allowZero: true });
}

function trimmedOrNull(text: string | null | undefined): string | null {
  const trimmed = (text ?? '').trim();
  return trimmed ? trimmed : null;
}

export function newSizeRow(
  overrides: Partial<ISizeRowForm> = {}
): ISizeRowForm {
  return {
    key: nextKey('s'),
    id: null,
    title: '',
    priceText: '',
    discountText: null,
    active: true,
    picture: null,
    description: null,
    ...overrides,
  };
}

export function linkRowFromOption(
  option: IOption,
  active = true
): IOptionLinkRow {
  return {
    key: nextKey('o'),
    optionId: option.id ?? null,
    draft: null,
    active,
    overrides: null,
    option,
  };
}

export function linkRowFromDraft(draft: ICreateOptionDto): IOptionLinkRow {
  return {
    key: nextKey('d'),
    optionId: null,
    draft,
    active: true,
    overrides: null,
    option: null,
  };
}

export function productFormFromItem(
  item: IItem,
  country: string
): IProductForm {
  const sizes = sortedSizes(item);
  const price: IPriceForm = sizes.length
    ? {
        mode: 'sizes',
        title: sizeOption(item)?.title ?? '',
        rows: sizes.map((size) =>
          newSizeRow({
            key: `s${size.id}`,
            id: size.id ?? null,
            title: size.title,
            priceText: moneyText(size.cost, country),
            discountText:
              size.discountedCost != null
                ? moneyText(size.discountedCost, country)
                : null,
            active: size.active,
            picture: size.picture ?? null,
            description: size.description ?? null,
          })
        ),
      }
    : {
        mode: 'single',
        priceText: moneyText(item.price, country),
        discountText:
          item.discountedPrice != null
            ? moneyText(item.discountedPrice, country)
            : null,
      };

  const links = [...(item.itemOptions ?? [])]
    .filter((link) => link.option && !link.option.main)
    .sort((a, b) => (a.index ?? 0) - (b.index ?? 0));

  return {
    name: item.name ?? '',
    description: item.description ?? '',
    categoryId: item.categoryId,
    picture: item.picture || null,
    allergens: item.allergens ?? 0,
    recipe: item.recipe ?? '',
    vatText: item.vat != null ? String(item.vat) : '',
    price,
    visible: item.visible,
    hiddenBranchIds: (item.hiddenBranchesIds ?? [])
      .map((id) => Number(id))
      .filter((id) => Number.isInteger(id))
      .sort((a, b) => a - b),
    hourRanges: item.hourRanges?.length
      ? item.hourRanges.map((range) => ({ ...range }))
      : null,
    optionLinks: links.map((link) => {
      const hasOverrides =
        link.min != null ||
        link.max != null ||
        link.mandatory != null ||
        link.multiple != null;
      return {
        key: `o${link.option?.id}`,
        optionId: link.option?.id ?? link.optionId ?? null,
        draft: null,
        active: link.active,
        overrides: hasOverrides
          ? {
              min: link.min ?? null,
              max: link.max ?? null,
              mandatory: link.mandatory ?? null,
              multiple: link.multiple ?? null,
            }
          : null,
        option: link.option,
      };
    }),
    comandaPrinters: item.comandaPrinters ?? null,
    comandaSplit: item.comandaSplit ?? ComandaSplit.None,
    popularHidden: !!item.popularHidden,
    reportingCategoryId: item.reportingCategoryId ?? null,
  };
}

/** The create form: basics and price only. */
export function emptyProductForm(categoryId: number, name = ''): IProductForm {
  return {
    name,
    description: '',
    categoryId,
    picture: null,
    allergens: 0,
    recipe: '',
    vatText: '',
    price: { mode: 'single', priceText: '', discountText: null },
    visible: true,
    hiddenBranchIds: [],
    hourRanges: null,
    optionLinks: [],
    comandaPrinters: null,
    comandaSplit: ComandaSplit.None,
    popularHidden: false,
    reportingCategoryId: null,
  };
}

//#region Comparison

function sizeRowValue(row: ISizeRowForm): unknown[] {
  return [
    row.id,
    row.title.trim(),
    readMoney(row.priceText),
    row.discountText === null ? null : readMoney(row.discountText),
    row.active,
    row.picture ?? null,
    trimmedOrNull(row.description),
  ];
}

function priceValue(price: IPriceForm): unknown {
  if (price.mode === 'single')
    return [
      'single',
      readMoney(price.priceText),
      price.discountText === null ? null : readMoney(price.discountText),
    ];
  return ['sizes', price.title.trim(), price.rows.map(sizeRowValue)];
}

/** A comparable value per key: equal values mean «no change». */
function keyValue(form: IProductForm, key: TProductFormKey): unknown {
  switch (key) {
    case 'name':
      return form.name.trim();
    case 'description':
      return trimmedOrNull(form.description);
    case 'recipe':
      return trimmedOrNull(form.recipe);
    case 'vatText':
      return trimmedOrNull(form.vatText) === null
        ? null
        : readMoney(form.vatText);
    case 'price':
      return priceValue(form.price);
    case 'hiddenBranchIds':
      return [...form.hiddenBranchIds].sort((a, b) => a - b);
    case 'hourRanges':
      return form.hourRanges?.length ? form.hourRanges : null;
    case 'optionLinks':
      return form.optionLinks.map((row) => [
        row.optionId,
        row.draft ? JSON.stringify(row.draft) : null,
        row.active,
      ]);
    default:
      return form[key];
  }
}

const PRODUCT_FORM_MODEL: IFormModel<IProductForm> = {
  keys: PRODUCT_FORM_KEYS,
  value: keyValue,
};

export function sameKey(
  a: IProductForm,
  b: IProductForm,
  key: TProductFormKey
): boolean {
  return sameFormKey(PRODUCT_FORM_MODEL, a, b, key);
}

/** Keys whose values differ between two forms. */
export function changedKeys(
  a: IProductForm,
  b: IProductForm
): TProductFormKey[] {
  return formChangedKeys(PRODUCT_FORM_MODEL, a, b);
}

//#endregion

//#region Rebase

/** `rows` mapped; the same array when no row changed. */
function mapRows<R>(rows: R[], map: (row: R) => R): R[] {
  let changed = false;
  const mapped = rows.map((row) => {
    const next = map(row);
    if (next !== row) changed = true;
    return next;
  });
  return changed ? mapped : rows;
}

/**
 * `form` with its size rows and group rows mapped. Unchanged parts keep
 * their identity: `backToSingle` in the price section compares the price
 * object after its question.
 */
function mapFormRows(
  form: IProductForm,
  size: (row: ISizeRowForm) => ISizeRowForm,
  link: (row: IOptionLinkRow) => IOptionLinkRow
): IProductForm {
  let result = form;
  if (form.price.mode === 'sizes') {
    const rows = mapRows(form.price.rows, size);
    if (rows !== form.price.rows)
      result = { ...result, price: { ...form.price, rows } };
  }
  const optionLinks = mapRows(form.optionLinks, link);
  if (optionLinks !== form.optionLinks) result = { ...result, optionLinks };
  return result;
}

/**
 * `form` with the ids of the rows the save created. A row the request sent
 * as new (its key was sent as `ref`) takes the created id, whatever id it
 * held: `sizesPatch` also sends as new a row whose id the base no longer
 * has. A draft becomes the created group, keeping its key and `active`. A
 * group picked meanwhile that is the created one is linked once (the first
 * row stays).
 */
function withCreatedIds(
  form: IProductForm,
  created: ICatalogCreatedRefs,
  saved: IProductForm
): IProductForm {
  const sizeIds = new Map(
    created.sizes.map((entry) => [entry.ref, entry.id] as const)
  );
  const groupIds = new Map(
    created.groups.map((entry) => [entry.ref, entry.id] as const)
  );
  const savedLinks = new Map(
    saved.optionLinks.flatMap((row) =>
      row.optionId !== null ? [[row.optionId, row] as const] : []
    )
  );
  const result = mapFormRows(
    form,
    (row) => {
      const id = sizeIds.get(row.key);
      return id !== undefined && id !== row.id ? { ...row, id } : row;
    },
    (row) => {
      const id = groupIds.get(row.key);
      const link =
        id !== undefined && id !== row.optionId
          ? savedLinks.get(id)
          : undefined;
      // A copy: `saved` becomes `base`, which must not share objects with `form`.
      return link ? { ...copy(link), key: row.key, active: row.active } : row;
    }
  );
  const seen = new Set<number>();
  const links = result.optionLinks.filter((row) => {
    if (row.optionId === null) return true;
    if (seen.has(row.optionId)) return false;
    seen.add(row.optionId);
    return true;
  });
  return links.length === result.optionLinks.length
    ? result
    : { ...result, optionLinks: links };
}

/**
 * `next` with the keys `current` gives the same rows (sizes by id, groups by
 * optionId). A size row sent as new while holding a live id the base did not
 * have keeps its key on the created row, so the row that still has that id
 * gets a fresh key (keys stay unique).
 */
function keepRowKeys(next: IProductForm, current: IProductForm): IProductForm {
  const sizeKeys = new Map(
    current.price.mode === 'sizes'
      ? current.price.rows.flatMap((row) =>
          row.id !== null ? [[row.id, row.key] as const] : []
        )
      : []
  );
  const linkKeys = new Map(
    current.optionLinks.flatMap((row) =>
      row.optionId !== null ? [[row.optionId, row.key] as const] : []
    )
  );
  const nextIds = new Set(listIds(next, 'price'));
  const carried = new Set(
    [...sizeKeys].flatMap(([id, key]) => (nextIds.has(id) ? [key] : []))
  );
  const rekey = <R extends { key: string }>(
    row: R,
    key: string | undefined
  ): R => (key !== undefined && key !== row.key ? { ...row, key } : row);
  return mapFormRows(
    next,
    (row) => {
      const key = row.id !== null ? sizeKeys.get(row.id) : undefined;
      if (key !== undefined) return rekey(row, key);
      return carried.has(row.key) ? { ...row, key: nextKey('s') } : row;
    },
    (row) =>
      rekey(row, row.optionId !== null ? linkKeys.get(row.optionId) : undefined)
  );
}

type TListKey = 'price' | 'optionLinks';

/** Ids of a list section (sizes by id, groups by optionId); new rows have none. */
function listIds(form: IProductForm, key: TListKey): number[] {
  if (key === 'optionLinks')
    return form.optionLinks.flatMap((row) =>
      row.optionId !== null ? [row.optionId] : []
    );
  return form.price.mode === 'sizes'
    ? form.price.rows.flatMap((row) => (row.id !== null ? [row.id] : []))
    : [];
}

/**
 * Whether another device changed the rows of a list during the save: the
 * saved list has a row that neither the request nor the owner's form has
 * (added elsewhere), or the owner's form still has a row the request sent
 * that the saved list no longer has (removed elsewhere).
 */
function changedElsewhere(
  key: TListKey,
  saved: IProductForm,
  request: IProductForm,
  mine: IProductForm
): boolean {
  const savedIds = new Set(listIds(saved, key));
  const requestIds = new Set(listIds(request, key));
  const mineIds = listIds(mine, key);
  const known = new Set([...requestIds, ...mineIds]);
  return (
    [...savedIds].some((id) => !known.has(id)) ||
    mineIds.some((id) => requestIds.has(id) && !savedIds.has(id))
  );
}

export interface IAfterSaveRebase {
  form: IProductForm;
  /**
   * Sections the owner changed during the save in which another device
   * added or removed rows meanwhile: the owner's list stays and «Guardar»
   * asks first.
   */
  conflicts: TProductFormKey[];
}

/**
 * After a save: rows the save created take their ids (`created`, by `ref`
 * = row key); keys the owner did not change during the request take the
 * saved value; keys they changed keep their value, and are conflicts when
 * another device added or removed rows of that list meanwhile. Row keys
 * are kept.
 */
export function rebaseAfterSave(
  form: IProductForm,
  sent: IProductForm,
  saved: IProductForm,
  created: ICatalogCreatedRefs
): IAfterSaveRebase {
  const mine = withCreatedIds(form, created, saved);
  const request = withCreatedIds(sent, created, saved);
  const conflicts: TProductFormKey[] = [];
  const next = PRODUCT_FORM_KEYS.reduce<IProductForm>((acc, key) => {
    if (sameKey(form, sent, key)) return { ...acc, [key]: copy(saved[key]) };
    if (
      (key === 'price' || key === 'optionLinks') &&
      changedElsewhere(key, saved, request, mine)
    )
      conflicts.push(key);
    return acc;
  }, mine);
  return { form: keepRowKeys(next, mine), conflicts };
}

/**
 * Keys a save did not send that another device changed while it ran
 * (`previous`, the base when the answer arrives, differs from `saved`) and
 * that the owner changed too: conflicts whichever came first, the answer or
 * the realtime update. The server never rewrites keys a request did not send.
 */
export function changedDuringSave(
  previous: IProductForm,
  saved: IProductForm,
  form: IProductForm,
  sentKeys: readonly TProductFormKey[]
): TProductFormKey[] {
  return changedKeys(saved, form).filter(
    (key) => !sentKeys.includes(key) && !sameKey(previous, saved, key)
  );
}

/** Another device changed the product: clean keys follow it, dirty ones stay; row keys are kept. */
export function rebaseOnRemote(
  base: IProductForm,
  form: IProductForm,
  remote: IProductForm
): IRemoteRebase<IProductForm> {
  const result = formRebaseOnRemote(PRODUCT_FORM_MODEL, base, form, remote);
  return { ...result, form: keepRowKeys(result.form, form) };
}

/** Size rows whose id no longer exists (removed elsewhere) become new rows. */
export function detachMissingSizes(
  form: IProductForm,
  item: IItem
): IProductForm {
  if (form.price.mode !== 'sizes') return form;
  const alive = new Set(sortedSizes(item).map((size) => size.id));
  return {
    ...form,
    price: {
      ...form.price,
      rows: form.price.rows.map((row) =>
        row.id !== null && !alive.has(row.id) ? { ...row, id: null } : row
      ),
    },
  };
}

//#endregion

//#region Validation

export type TTextError = 'required' | 'tooLong';

export interface ISizeRowErrors {
  title?: TTextError;
  price?: TPriceFieldError;
  discount?: TPriceFieldError;
}

export interface IProductFormErrors {
  name?: TTextError;
  price?: TPriceFieldError;
  discount?: TPriceFieldError;
  sizesTitle?: TTextError;
  sizes?: 'noActive' | 'none';
  sizeRows?: Record<string, ISizeRowErrors>;
  vat?: 'invalid';
}

function textError(text: string, max: number): TTextError | undefined {
  const trimmed = text.trim();
  if (!trimmed) return 'required';
  return trimmed.length > max ? 'tooLong' : undefined;
}

function moneyError(text: string): TPriceFieldError | undefined {
  if (!text.trim()) return 'required';
  return readMoney(text) === null ? 'invalid' : undefined;
}

function discountError(
  priceText: string,
  discountText: string | null,
  changed: boolean
): TPriceFieldError | undefined {
  if (discountText === null) return undefined;
  const error = moneyError(discountText);
  if (error) return error;
  const price = readMoney(priceText);
  const discount = readMoney(discountText);
  return changed && price !== null && discount !== null && discount >= price
    ? 'discountTooHigh'
    : undefined;
}

/** Errors of a form; `base` tells which price lines the owner changed. */
export function validateProductForm(
  form: IProductForm,
  base: IProductForm | null
): IProductFormErrors {
  const errors: IProductFormErrors = {};
  const name = textError(form.name, MAX_NAME_LENGTH);
  if (name) errors.name = name;

  if (form.vatText.trim()) {
    const vat = readMoney(form.vatText);
    if (vat === null || vat > 100) errors.vat = 'invalid';
  }

  if (form.price.mode === 'single') {
    const price = moneyError(form.price.priceText);
    if (price) errors.price = price;
    const basePrice = base?.price.mode === 'single' ? base.price : null;
    const changed =
      !basePrice ||
      readMoney(basePrice.priceText) !== readMoney(form.price.priceText) ||
      basePrice.discountText === null ||
      form.price.discountText === null ||
      readMoney(basePrice.discountText) !== readMoney(form.price.discountText);
    const discount = discountError(
      form.price.priceText,
      form.price.discountText,
      changed
    );
    if (discount) errors.discount = discount;
    return errors;
  }

  const sizes = form.price;
  const title = textError(sizes.title, MAX_SIZE_TITLE_LENGTH);
  if (title) errors.sizesTitle = title;
  if (!sizes.rows.length) errors.sizes = 'none';
  else if (!sizes.rows.some((row) => row.active)) errors.sizes = 'noActive';

  const baseRows = new Map(
    base?.price.mode === 'sizes'
      ? base.price.rows.map((row) => [row.id, row] as const)
      : []
  );
  const rowErrors: Record<string, ISizeRowErrors> = {};
  for (const row of sizes.rows) {
    const errorsOfRow: ISizeRowErrors = {};
    const rowTitle = textError(row.title, MAX_SIZE_TITLE_LENGTH);
    if (rowTitle) errorsOfRow.title = rowTitle;
    const price = moneyError(row.priceText);
    if (price) errorsOfRow.price = price;
    const stored = row.id !== null ? baseRows.get(row.id) : undefined;
    const changed =
      !stored ||
      readMoney(stored.priceText) !== readMoney(row.priceText) ||
      stored.discountText === null ||
      row.discountText === null ||
      readMoney(stored.discountText) !== readMoney(row.discountText);
    const discount = discountError(row.priceText, row.discountText, changed);
    if (discount) errorsOfRow.discount = discount;
    if (Object.keys(errorsOfRow).length) rowErrors[row.key] = errorsOfRow;
  }
  if (Object.keys(rowErrors).length) errors.sizeRows = rowErrors;
  return errors;
}

export function hasErrors(errors: IProductFormErrors): boolean {
  return Object.keys(errors).length > 0;
}

//#endregion

//#region Payloads

function sameList(a: readonly number[] | null, b: readonly number[] | null) {
  return JSON.stringify(a) === JSON.stringify(b);
}

function sizesPatch(
  base: IProductForm,
  form: Extract<IPriceForm, { mode: 'sizes' }>
): IItemPatch['sizes'] {
  const baseRows = new Map(
    base.price.mode === 'sizes'
      ? base.price.rows.map((row) => [row.id, row] as const)
      : []
  );
  const upsert: ISizeUpsert[] = form.rows.map((row, index) => {
    const cost = readMoney(row.priceText) ?? 0;
    const discountedCost =
      row.discountText === null ? null : readMoney(row.discountText);
    const stored = row.id !== null ? baseRows.get(row.id) : undefined;
    if (!stored || row.id === null)
      return {
        ref: row.key,
        title: row.title.trim(),
        cost,
        discountedCost,
        active: row.active,
        index,
        picture: row.picture,
        description: trimmedOrNull(row.description),
      };

    const patch: ISizeUpsert = { id: row.id, index };
    if (stored.title.trim() !== row.title.trim())
      patch.title = row.title.trim();
    if (readMoney(stored.priceText) !== cost) patch.cost = cost;
    const storedDiscount =
      stored.discountText === null ? null : readMoney(stored.discountText);
    if (storedDiscount !== discountedCost)
      patch.discountedCost = discountedCost;
    if (stored.active !== row.active) patch.active = row.active;
    if ((stored.picture ?? null) !== (row.picture ?? null))
      patch.picture = row.picture;
    if (trimmedOrNull(stored.description) !== trimmedOrNull(row.description))
      patch.description = trimmedOrNull(row.description);
    return patch;
  });

  const kept = new Set(form.rows.map((row) => row.id));
  const remove = [...baseRows.keys()].filter(
    (id): id is number => id !== null && !kept.has(id)
  );
  const patch: NonNullable<IItemPatch['sizes']> = { upsert };
  if (
    base.price.mode !== 'sizes' ||
    base.price.title.trim() !== form.title.trim()
  )
    patch.title = form.title.trim();
  if (remove.length) patch.remove = remove;
  return patch;
}

function pricePatch(
  base: IProductForm,
  form: IProductForm
): Partial<IItemPatch> {
  const price = form.price;
  if (price.mode === 'sizes') return { sizes: sizesPatch(base, price) };

  const patch: Partial<IItemPatch> = {};
  const value = readMoney(price.priceText) ?? 0;
  const discount =
    price.discountText === null ? null : readMoney(price.discountText);
  if (base.price.mode === 'sizes') {
    // Always both: the stored discount mirrors the old first size.
    patch.sizes = null;
    patch.price = value;
    patch.discountedPrice = discount;
    return patch;
  }
  if (readMoney(base.price.priceText) !== value) patch.price = value;
  const baseDiscount =
    base.price.discountText === null
      ? null
      : readMoney(base.price.discountText);
  if (baseDiscount !== discount) patch.discountedPrice = discount;
  return patch;
}

function optionLinksInput(form: IProductForm): IOptionLinkInput[] {
  return form.optionLinks.map((row, index) =>
    row.draft
      ? { draft: row.draft, active: row.active, index, ref: row.key }
      : { optionId: row.optionId ?? undefined, active: row.active, index }
  );
}

/** The `PATCH /v2/items/:id` body of the changed keys; null when nothing changed. */
export function diffProductForm(
  base: IProductForm,
  form: IProductForm
): IItemPatch | null {
  const keys = changedKeys(base, form);
  if (!keys.length) return null;
  const patch: IItemPatch = {};

  for (const key of keys)
    switch (key) {
      case 'name':
        patch.name = form.name.trim();
        break;
      case 'description':
        patch.description = trimmedOrNull(form.description);
        break;
      case 'categoryId':
        patch.categoryId = form.categoryId;
        break;
      case 'picture':
        patch.picture = form.picture;
        break;
      case 'allergens':
        patch.allergens = form.allergens;
        break;
      case 'recipe':
        patch.recipe = trimmedOrNull(form.recipe);
        break;
      case 'vatText':
        patch.vat = trimmedOrNull(form.vatText)
          ? readMoney(form.vatText)
          : null;
        break;
      case 'price':
        Object.assign(patch, pricePatch(base, form));
        break;
      case 'hourRanges':
        patch.hourRanges = form.hourRanges?.length ? form.hourRanges : null;
        break;
      case 'optionLinks':
        patch.optionLinks = optionLinksInput(form);
        patch.baseOptionIds = base.optionLinks.flatMap((row) =>
          row.optionId !== null ? [row.optionId] : []
        );
        break;
      case 'comandaPrinters':
        if (!sameList(base.comandaPrinters, form.comandaPrinters))
          patch.comandaPrinters = form.comandaPrinters;
        break;
      case 'comandaSplit':
        patch.comandaSplit = form.comandaSplit;
        break;
      case 'popularHidden':
        patch.popularHidden = form.popularHidden;
        break;
      case 'reportingCategoryId':
        patch.reportingCategoryId = form.reportingCategoryId;
        break;
      case 'visible':
      case 'hiddenBranchIds':
        break;
    }

  if (keys.includes('visible') || keys.includes('hiddenBranchIds')) {
    const hidden = new Set(form.hiddenBranchIds);
    const baseHidden = new Set(base.hiddenBranchIds);
    const hideInBranchIds = form.hiddenBranchIds.filter(
      (id) => !baseHidden.has(id)
    );
    const showInBranchIds = base.hiddenBranchIds.filter(
      (id) => !hidden.has(id)
    );
    patch.availability = {
      ...(base.visible !== form.visible ? { visible: form.visible } : {}),
      ...(hideInBranchIds.length ? { hideInBranchIds } : {}),
      ...(showInBranchIds.length ? { showInBranchIds } : {}),
    };
  }

  return Object.keys(patch).length ? patch : null;
}

/** The `POST /v2/items` body of the create form. */
export function createPayloadFromForm(
  form: IProductForm,
  restaurantId: number
): IItemCreate {
  const payload: IItemCreate = {
    restaurantId,
    categoryId: form.categoryId,
    name: form.name.trim(),
  };
  const description = trimmedOrNull(form.description);
  if (description) payload.description = description;
  if (form.picture) payload.picture = form.picture;
  if (form.price.mode === 'single') {
    payload.price = readMoney(form.price.priceText) ?? 0;
    if (form.price.discountText !== null)
      payload.discountedPrice = readMoney(form.price.discountText);
  } else {
    payload.sizes = sizesPatch(emptyProductForm(form.categoryId), form.price);
  }
  if (!form.visible) payload.availability = { visible: false };
  return payload;
}

//#endregion
